import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Search, X } from "lucide-react";

/**
 * LinkPicker — choose link_type (none | category | product | custom) and link_target.
 * Resolves category options from /categories and offers a typeahead search via
 * /admin/products/search for products. For custom, freeform URL input.
 *
 * Props:
 *  - value     : { link_type, link_target }
 *  - onChange  : (next:{link_type, link_target}) => void
 *  - allowNone : default true
 */
export default function LinkPicker({ value, onChange, allowNone = true }) {
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [productLabel, setProductLabel] = useState("");

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data || [])); }, []);

  const linkType = value?.link_type || "none";
  const linkTarget = value?.link_target || "";

  // Resolve existing product label
  useEffect(() => {
    if (linkType === "product" && linkTarget && !productLabel) {
      api.get(`/admin/products/search?q=${encodeURIComponent(linkTarget)}`).then((r) => {
        const hit = (r.data || []).find((p) => p.id === linkTarget || p.seo_slug === linkTarget);
        if (hit) setProductLabel(`${hit.name_tr} (${hit.sku})`);
      }).catch(() => {});
    }
  }, [linkType, linkTarget, productLabel]);

  const update = (patch) => onChange({ link_type: linkType, link_target: linkTarget, ...patch });

  const doSearch = async (q) => {
    setSearch(q);
    if (q.length < 2) { setResults([]); return; }
    const r = await api.get(`/admin/products/search?q=${encodeURIComponent(q)}`);
    setResults(r.data || []);
  };

  const flatCats = (() => {
    // Build "Parent / Child" labels
    const byId = Object.fromEntries(cats.map((c) => [c.id, c]));
    const path = (c) => {
      const out = [c.name_tr];
      let cur = c;
      while (cur.parent_id && byId[cur.parent_id]) {
        cur = byId[cur.parent_id];
        out.unshift(cur.name_tr);
      }
      return out.join(" / ");
    };
    return [...cats].sort((a, b) => path(a).localeCompare(path(b))).map((c) => ({ id: c.id, label: path(c) }));
  })();

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {allowNone && (
          <TypeBtn active={linkType === "none"} onClick={() => update({ link_type: "none", link_target: "" })} testid="linkpicker-type-none">Bağlantısız</TypeBtn>
        )}
        <TypeBtn active={linkType === "category"} onClick={() => update({ link_type: "category", link_target: "" })} testid="linkpicker-type-category">Kategori</TypeBtn>
        <TypeBtn active={linkType === "product"} onClick={() => { setProductLabel(""); update({ link_type: "product", link_target: "" }); }} testid="linkpicker-type-product">Ürün</TypeBtn>
        <TypeBtn active={linkType === "custom"} onClick={() => update({ link_type: "custom", link_target: "" })} testid="linkpicker-type-custom">Özel URL</TypeBtn>
      </div>

      {linkType === "category" && (
        <select data-testid="linkpicker-category-select" value={linkTarget} onChange={(e) => update({ link_target: e.target.value })}
          className="w-full border border-border px-3 py-2 text-sm bg-white">
          <option value="">— Kategori seçin —</option>
          {flatCats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      )}

      {linkType === "product" && (
        <div className="relative">
          {linkTarget && productLabel ? (
            <div className="flex items-center gap-2 border border-border px-3 py-2 bg-[#FAF8F3]">
              <span data-testid="linkpicker-product-label" className="text-sm flex-1">{productLabel}</span>
              <button type="button" onClick={() => { setProductLabel(""); update({ link_target: "" }); }} className="p-1 hover:bg-white"><X size={14} /></button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 border border-border px-3 py-2">
                <Search size={14} className="text-[#71717A]" />
                <input data-testid="linkpicker-product-search" type="text" value={search} onChange={(e) => doSearch(e.target.value)} placeholder="Ürün adı, SKU veya slug ara…"
                  className="flex-1 outline-none text-sm bg-transparent" />
              </div>
              {results.length > 0 && (
                <ul className="border border-border border-t-0 max-h-60 overflow-y-auto bg-white shadow">
                  {results.map((p) => (
                    <li key={p.id}>
                      <button type="button" data-testid={`linkpicker-product-pick-${p.id}`}
                        onClick={() => { setProductLabel(`${p.name_tr} (${p.sku})`); setResults([]); setSearch(""); update({ link_target: p.id }); }}
                        className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-[#FAF8F3] text-sm">
                        {p.thumb ? <img src={p.thumb} alt="" className="w-10 h-10 object-cover" /> : <span className="w-10 h-10 bg-[#FAF8F3]" />}
                        <span className="flex-1">
                          <span className="block font-medium">{p.name_tr}</span>
                          <span className="text-[10px] uppercase tracking-wider text-[#71717A]">{p.sku} · {p.status}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      {linkType === "custom" && (
        <input data-testid="linkpicker-custom-url" type="text" value={linkTarget} onChange={(e) => update({ link_target: e.target.value })}
          placeholder="https://… veya /products?category=..." className="w-full border border-border px-3 py-2 text-sm bg-white" />
      )}
    </div>
  );
}

function TypeBtn({ active, onClick, children, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={"px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] border " + (active ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-border text-[#52525B] hover:bg-[#FAF8F3]")}>
      {children}
    </button>
  );
}
